import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { InformationCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useConversations } from '@/context/ConversationsContext';
import type { Conversation } from '@/hooks/useAllConversations';
import useMemberPhotoMap from '@/hooks/useMemberPhotoMap';
import ConversationSidebar from './sidebar/ConversationSidebar';
import ConversationPane from './conversation/ConversationPane';
import NewChatMenu from './sidebar/NewChatModals';
import ConversationDetailsPanel from './details/ConversationDetailsPanel';
import ChatSearchPopover from './search/ChatSearchPopover';

/**
 * Chat workspace: conversation sidebar + active conversation pane + details.
 * Routes: /chat (auto-select most recent), /chat/:groupId (Everyone),
 * /chat/:groupId/:chatGroupId (named chat group or DM).
 */
export default function ChatLayout() {
  const { groupId, chatGroupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the auto-select navigate below — the pane delays mark-as-read so
  // the unread badge doesn't vanish before the user sees it.
  const autoSelected = Boolean(
    (location.state as { autoSelected?: boolean } | null)?.autoSelected
  );
  const {
    currentOrganization,
    currentGroup,
    switchOrganization,
    isSwitchingOrg,
    setCurrentGroup,
    organizations,
  } = useAuth();
  const { conversations: rawConversations, loading, error, refresh } = useConversations();
  const photoMap = useMemberPhotoMap();

  // Overlay fresh presigned photos onto DM rows — the Firestore
  // dmParticipants copies are expired presigns (mobile does the same).
  const conversations = useMemo(
    () =>
      rawConversations.map((c) => {
        if (c.type !== 'dm' || !c.dmPartnerUserId) return c;
        const fresh = photoMap.byId.get(c.dmPartnerUserId);
        return fresh && fresh !== c.avatarUrl ? { ...c, avatarUrl: fresh } : c;
      }),
    [rawConversations, photoMap]
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchJumpId, setSearchJumpId] = useState<string | null>(null);

  const selectedKey = chatGroupId ?? (groupId ? `everyone_${groupId}` : null);
  const selected = useMemo(
    () => conversations.find((c) => c.key === selectedKey) ?? null,
    [conversations, selectedKey]
  );

  // Auto-select the most recent conversation when landing on bare /chat,
  // and heal stale URLs (archived/left conversations) back to /chat. The
  // heal waits a few seconds: a freshly created DM/chat group is navigated
  // to BEFORE the membership listener delivers it, and an instant redirect
  // would bounce the user away from their brand-new conversation.
  useEffect(() => {
    if (loading) return;
    if (!selectedKey && conversations.length > 0) {
      const first = conversations[0];
      navigate(
        first.chatGroupId ? `/chat/${first.groupId}/${first.chatGroupId}` : `/chat/${first.groupId}`,
        { replace: true, state: { autoSelected: true } }
      );
      return;
    }
    if (selectedKey && conversations.length > 0 && !selected) {
      const healTimer = setTimeout(() => navigate('/chat', { replace: true }), 5000);
      return () => clearTimeout(healTimer);
    }
  }, [selectedKey, selected, loading, conversations, navigate]);

  // Deep links / refreshes can land on a cross-org conversation without going
  // through handleSelect — rotate the org context so the pane can mount.
  useEffect(() => {
    if (
      selected &&
      currentOrganization &&
      selected.organizationId !== currentOrganization.id &&
      !isSwitchingOrg
    ) {
      switchOrganization(selected.organizationId, selected.groupId).catch((e) =>
        console.warn('[Chat] Org switch for deep link failed:', e)
      );
    }
  }, [selected, currentOrganization, isSwitchingOrg, switchOrganization]);

  const openConversation = useCallback(
    (gId: string, cgId?: string | null) => {
      navigate(cgId ? `/chat/${gId}/${cgId}` : `/chat/${gId}`);
    },
    [navigate]
  );

  const handleSelect = useCallback(
    async (conv: Conversation) => {
      openConversation(conv.groupId, conv.chatGroupId);

      // Cross-org conversation: rotate org context (claims) so sends work.
      // Reads already work — rules are membership-gated, not claim-gated.
      if (conv.organizationId !== currentOrganization?.id) {
        try {
          await switchOrganization(conv.organizationId, conv.groupId);
        } catch (e) {
          console.warn('[Chat] Org switch on conversation select failed:', e);
        }
      } else {
        // Keep the app-level current group in sync with the conversation
        const membership = organizations.find((o) => o.organization_id === conv.organizationId);
        const group = membership?.groups.find((g) => g.id === conv.groupId);
        if (group && group.id !== currentGroup?.id) setCurrentGroup(group);
      }
    },
    [
      openConversation,
      currentOrganization?.id,
      switchOrganization,
      organizations,
      setCurrentGroup,
      currentGroup?.id,
    ]
  );

  // Everyone-scope AI search returns matches from ALL threads in the group.
  // If the clicked result lives in a different thread, open that conversation
  // first (mobile passes the message's chatGroupId the same way). The jump id
  // is held as PENDING until the route commits: react-router defers navigation
  // in a transition, so setting searchJumpId immediately would let the OLD
  // pane consume it against the wrong thread (context fetch 404s silently).
  const [pendingJump, setPendingJump] = useState<{ key: string; messageId: string } | null>(null);

  const handleSearchJump = useCallback(
    (messageId: string, messageChatGroupId: string | null) => {
      if (!selected || (selected.chatGroupId ?? null) === messageChatGroupId) {
        setSearchJumpId(messageId);
        return;
      }
      const conv = conversations.find(
        (c) => c.groupId === selected.groupId && (c.chatGroupId ?? null) === messageChatGroupId
      );
      if (!conv) return;
      setPendingJump({ key: conv.key, messageId });
      void handleSelect(conv);
    },
    [selected, conversations, handleSelect]
  );

  useEffect(() => {
    if (pendingJump && selected?.key === pendingJump.key) {
      setSearchJumpId(pendingJump.messageId);
      setPendingJump(null);
    }
  }, [pendingJump, selected?.key]);

  return (
    <div className="flex h-full">
      <ConversationSidebar
        conversations={conversations}
        loading={loading}
        error={error}
        selectedKey={selectedKey}
        onSelect={handleSelect}
        headerAction={
          <NewChatMenu
            groupId={currentGroup?.id ?? selected?.groupId ?? null}
            onOpenConversation={openConversation}
            onCreated={refresh}
          />
        }
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {selected ? (
          <div className="flex h-full min-h-0 flex-col">
            <header className="flex items-center gap-3 border-b border-flock-gray-200 bg-white px-4 py-3">
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold text-flock-navy-900">
                  {selected.name}
                </h2>
                <p className="truncate text-xs text-flock-navy-400">
                  {selected.groupName}
                  {selected.memberCount ? ` · ${selected.memberCount} members` : ''}
                  {isSwitchingOrg ? ' · switching…' : ''}
                </p>
              </div>
              <button
                onClick={() => setSearchOpen((v) => !v)}
                title="Search this conversation"
                className={clsx(
                  'rounded-lg p-2 transition hover:bg-flock-navy-50',
                  searchOpen ? 'text-flock-navy-700' : 'text-flock-navy-400'
                )}
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setDetailsOpen((v) => !v)}
                title="Conversation details"
                className={clsx(
                  'rounded-lg p-2 transition hover:bg-flock-navy-50',
                  detailsOpen ? 'text-flock-navy-700' : 'text-flock-navy-400'
                )}
              >
                <InformationCircleIcon className="h-5 w-5" />
              </button>
            </header>
            {searchOpen && (
              <div className="relative">
                <ChatSearchPopover
                  groupId={selected.groupId}
                  chatGroupId={selected.chatGroupId}
                  onJumpTo={handleSearchJump}
                  onClose={() => setSearchOpen(false)}
                />
              </div>
            )}
            <div className="flex min-h-0 flex-1">
              {currentOrganization?.id === selected.organizationId ? (
                <ConversationPane
                  key={selected.key}
                  conversation={selected}
                  autoSelected={autoSelected}
                  searchJumpId={searchJumpId}
                  onSearchJumpConsumed={() => setSearchJumpId(null)}
                />
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-flock-navy-400">
                  Switching organization…
                </div>
              )}
              {detailsOpen && (
                <ConversationDetailsPanel
                  conversation={selected}
                  onClose={() => setDetailsOpen(false)}
                  onLeftConversation={() => {
                    setDetailsOpen(false);
                    navigate('/chat');
                  }}
                  onConversationsChanged={refresh}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            {loading ? 'Loading conversations…' : 'Select a conversation'}
          </div>
        )}
      </div>
    </div>
  );
}
